import { useCallback, useEffect, useRef, useState } from 'react'

import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import {
  useAppRoutes,
  useLocationHelper,
  useProcurifyIntl,
} from '@webapp/hooks'
import { type TravelListStatus } from '@webapp/modules/expense/business-logic/types/TravelListStatus'
import { type TravelStatusCounts } from '@webapp/modules/expense/business-logic/types/TravelStatusCounts'
import { useBatchApproveTravelsPresenter } from '@webapp/modules/expense/features/batch-approve-travels/presenters/BatchApproveTravelsPresenter'
import { useBatchDenyTravelsPresenter } from '@webapp/modules/expense/features/batch-deny-travels/presenters/BatchDenyTravelsPresenter'
import {
  type ApprovableTravelListPresenterRef,
  type TravelApprovalSelection,
} from '@webapp/modules/expense/features/list-approvable-travels/presenters/ApprovableTravelListPresenter'
import { fetchCounts } from '@webapp/stores'
import messages from './messages'
import {
  type TravelApprovalsPageParams,
  TravelApprovalsPageTabStatuses,
} from '../../pages/types'
import { travelQueryConfig } from '../../query-configs/travel'

const toRouteParamId = (value: unknown): number | undefined => {
  if (typeof value !== 'string') return undefined

  const id = Number(value)

  return Number.isFinite(id) ? id : undefined
}

export const useTravelApprovalListView = () => {
  const intl = useProcurifyIntl()
  const dispatch = useDispatch()
  const { url } = useAppRoutes()
  const { search, modifySearch } = useLocationHelper()

  const { status = TravelApprovalsPageTabStatuses.PENDING } =
    useParams<TravelApprovalsPageParams>()

  // Tab count badges — fed by the list presenter's onCountsChange callback.
  const [counts, setCounts] = useState<Nullable<TravelStatusCounts>>(null)

  // FS-owned selection payload — fed by the list presenter's
  // onSelectionChange callback (checkboxes + per-row approver picks).
  const [selectionPayload, setSelectionPayload] = useState<
    TravelApprovalSelection[]
  >([])

  const presenterRef = useRef<ApprovableTravelListPresenterRef>(null)

  // BHV-036: the "Viewing as" select is shown only when subscribers exist.
  const { data: hasWatchlistApprovers = false } = useQuery({
    ...travelQueryConfig.hasWatchlistApprovers(),
    staleTime: Infinity,
  })

  // "?subscribed" query param synced with the watchlist select (BHV-036)
  const subscribedApproverId = toRouteParamId(search('subscribed'))

  // Post-approve/deny highlight (route query param, twin `TravelStatusView`)
  const justUpdatedId = toRouteParamId(search('justUpdated'))

  // BHV-032: selection only on the Pending tab without a watchlist filter
  const isSelectable =
    status === TravelApprovalsPageTabStatuses.PENDING && !subscribedApproverId

  const clearSelection = useCallback(() => {
    presenterRef.current?.clearSelection()
    setSelectionPayload([])
  }, [])

  useEffect(() => {
    clearSelection()
  }, [status, clearSelection])

  const {
    commands: { batchApprove },
    states: { isSubmitting: isApproving },
  } = useBatchApproveTravelsPresenter()

  const {
    commands: { batchDeny },
    states: { isSubmitting: isDenying },
  } = useBatchDenyTravelsPresenter()

  // BHV-034: success toast, selection clears, list refreshes (invalidation),
  // navigation counter updates.
  const onApprove = useCallback(async () => {
    try {
      await batchApprove(selectionPayload)
      clearSelection()
      dispatch(fetchCounts())
      toast.success({
        title: intl.formatMessage(messages.approveSuccessToastTitle),
        children: intl.formatMessage(messages.approveSuccessToastMessage),
      })
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.approveErrorToastTitle),
        children:
          getMessageFromError(err) ||
          intl.formatMessage(messages.approveErrorToastMessage),
      })
    }
  }, [batchApprove, selectionPayload, clearSelection, dispatch, intl])

  // BHV-035: success toast + list refresh
  const onDeny = useCallback(async () => {
    try {
      await batchDeny(selectionPayload.map((item) => item.travelId))
      clearSelection()
      dispatch(fetchCounts())
      toast.success({
        title: intl.formatMessage(messages.denySuccessToastTitle),
        children: intl.formatMessage(messages.denySuccessToastMessage),
      })
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.denyErrorToastTitle),
        children:
          getMessageFromError(err) ||
          intl.formatMessage(messages.denyErrorToastMessage),
      })
    }
  }, [batchDeny, selectionPayload, clearSelection, dispatch, intl])

  const onSelectionChange = useCallback(
    (payload: TravelApprovalSelection[]) => {
      setSelectionPayload(payload)
    },
    []
  )

  const onCountsChange = useCallback((newCounts: TravelStatusCounts) => {
    setCounts(newCounts)
  }, [])

  const onWatchlistChange = useCallback(
    (approverId: Nullable<number>) => {
      clearSelection()
      modifySearch({ subscribed: approverId ?? undefined, page: 1 })
    },
    [clearSelection, modifySearch]
  )

  const tabs = [
    {
      key: TravelApprovalsPageTabStatuses.ALL,
      label: intl.formatMessage(messages.allTabLabel),
      to: url('TravelApprovalsPage', {
        status: TravelApprovalsPageTabStatuses.ALL,
      }),
      count: counts?.all ?? 0,
    },
    {
      key: TravelApprovalsPageTabStatuses.PENDING,
      label: intl.formatMessage(messages.pendingTabLabel),
      to: url('TravelApprovalsPage', {
        status: TravelApprovalsPageTabStatuses.PENDING,
      }),
      count: counts?.pending ?? 0,
    },
    {
      key: TravelApprovalsPageTabStatuses.APPROVED,
      label: intl.formatMessage(messages.approvedTabLabel),
      to: url('TravelApprovalsPage', {
        status: TravelApprovalsPageTabStatuses.APPROVED,
      }),
      count: counts?.approved ?? 0,
    },
    {
      key: TravelApprovalsPageTabStatuses.DENIED,
      label: intl.formatMessage(messages.deniedTabLabel),
      to: url('TravelApprovalsPage', {
        status: TravelApprovalsPageTabStatuses.DENIED,
      }),
      count: counts?.denied ?? 0,
    },
  ]

  return {
    presenterRef,
    states: {
      status: status as TravelListStatus,
      subscribedApproverId,
      justUpdatedId,
      isSelectable,
      hasWatchlistApprovers,
      isSubmitting: isApproving || isDenying,
      tabs,
    },
    methods: {
      onApprove,
      onDeny,
      onSelectionChange,
      onCountsChange,
      onWatchlistChange,
    },
  }
}
